import React, { useState } from "react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

/**
 * Shared tabbed Events / Transactions selector menu.
 * Used by both Insights (MetricBlock) and Funnels (FunnelQueryConfig).
 *
 * Props:
 * - availableEvents: array of { name, source?, type?, count? }
 * - onSelect(name, source): called when user picks an item
 * - onClose(): optional, called after selection to close the menu
 * - maxHeight: optional max height for scrollable list (px)
 */
export default function EventsSelectorMenu({
  availableEvents = [],
  onSelect,
  onClose = null,
  maxHeight = 220,
  showSourceTabs = true,
}) {
  const [eventSelectorTab, setEventSelectorTab] = useState("all"); // 'all' | 'events' | 'transactions'

  const eventsList = Array.isArray(availableEvents) ? availableEvents : [];

  const getEventsForCurrentTab = () => {
    if (!showSourceTabs) return eventsList;
    return eventsList.filter((evt) => {
      const src = evt.source || (evt.type === "transaction" ? "transaction" : "event");
      if (eventSelectorTab === "events") return src === "event";
      if (eventSelectorTab === "transactions") return src === "transaction";
      return true; // 'all'
    });
  };

  const handleSelect = (evt) => {
    if (!onSelect) return;
    const src = evt.source || (evt.type === "transaction" ? "transaction" : "event");
    const name = evt.name || evt.id;
    onSelect(name, src);
    if (onClose) onClose();
  };

  return (
    <div
      className="border rounded-lg shadow-xl overflow-hidden flex flex-col"
      style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, maxHeight }}
    >
      {/* Tabs: All | Events | Transactions */}
      {showSourceTabs && (
        <div className="flex border-b flex-shrink-0" style={{ borderColor: theme.border }}>
          {[
            { id: "all", label: "All" },
            { id: "events", label: "Events" },
            { id: "transactions", label: "Transactions" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setEventSelectorTab(tab.id)}
              className="flex-1 px-2 py-2 text-xs font-medium transition-colors"
              style={{
                color: eventSelectorTab === tab.id ? theme.primary : theme.textMuted,
                borderBottom:
                  eventSelectorTab === tab.id
                    ? `2px solid ${theme.primary}`
                    : "2px solid transparent",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      <div className="py-1 overflow-y-auto" style={{ maxHeight }}>
        {getEventsForCurrentTab().length > 0 ? (
          getEventsForCurrentTab().map((evt) => {
            const src = evt.source || (evt.type === "transaction" ? "transaction" : "event");
            const tag = src === "transaction" ? "T" : "E";
            const name = evt.name || evt.id;
            return (
              <button
                key={`${name}\0${src}`}
                type="button"
                onClick={() => handleSelect(evt)}
                className="w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2"
                style={{ color: "#374151" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(45, 45, 45, 0.12)";
                  e.currentTarget.style.color = theme.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = theme.textSecondary;
                }}
              >
                <span className="flex-1 truncate">{name}</span>
                <span
                  className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded flex-shrink-0"
                  style={{
                    backgroundColor:
                      src === "transaction"
                        ? "rgba(75, 87, 170, 0.2)"
                        : "rgba(107, 114, 128, 0.2)",
                    color: src === "transaction" ? theme.primary : theme.textMuted,
                  }}
                  title={src === "transaction" ? "Transaction" : "Event"}
                >
                  {tag}
                </span>
                {evt.count != null && (
                  <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
                    {formatNumber(evt.count)}
                  </span>
                )}
              </button>
            );
          })
        ) : (
          <div className="px-3 py-2 text-sm" style={{ color: theme.textMuted }}>
            No options in this tab
          </div>
        )}
      </div>
    </div>
  );
}

